import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MapPin, Package as PackageIcon, Settings2, LogOut, Loader2,
  Inbox, MessageSquareText, Users2, Send, Image as ImageIcon, Copy, Trash2, Upload, X, Plus,
  Star, ArrowUp, ArrowDown, MessageSquareQuote,
} from "lucide-react";
import api from "../lib/api";
import { getFileUrl } from "@/lib/utils";

const INQUIRY_STATUSES = ["new", "contacted", "quoted", "won", "closed"];
const CONTENT_STATUSES = ["Available", "Limited", "Sold Out", "Coming Soon"];
const TABS = [
  { key: "inquiries", label: "Inquiries", icon: Inbox },
  { key: "destinations", label: "Destinasi", icon: MapPin },
  { key: "packages", label: "Paket", icon: PackageIcon },
  { key: "media", label: "Media", icon: ImageIcon },
  { key: "testimonials", label: "Testimoni", icon: MessageSquareQuote },
  { key: "settings", label: "Settings", icon: Settings2 },
];

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input type={type} className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

function ImageUpload({ value, onChange, testid, label = "Foto (hero image)" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Foto berhasil diupload.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal. Coba lagi.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex items-start gap-3">
        <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200">
          {value && <img src={value} alt="Preview" className="h-full w-full object-cover" data-testid={`${testid}-preview`} />}
        </div>
        <div className="flex-1">
          <label className="btn btn-outline !px-4 !py-2 !text-xs cursor-pointer" data-testid={testid}>
            {uploading ? "Mengupload..." : <><Upload className="h-3.5 w-3.5" /> Upload Foto</>}
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} disabled={uploading} data-testid={`${testid}-file-input`} />
          </label>
          <input
            className="field mt-2 !text-xs"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="atau paste URL gambar..."
            data-testid={`${testid}-url`}
          />
        </div>
      </div>
    </div>
  );
}

function GalleryEditor({ items = [], onChange, testid = "admin-edit-gallery" }) {
  const [uploading, setUploading] = useState(false);

  const add = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd);
      onChange([...items, data.url]);
      toast.success("Foto ditambahkan ke galeri.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="label">Galeri Foto</label>
      <div className="grid grid-cols-3 gap-2">
        {items.map((url, i) => (
          <div key={`${url}-${i}`} className="group relative overflow-hidden rounded-lg">
            <img src={url} alt="" className="h-16 w-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(items.filter((_, x) => x !== i))}
              className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100"
              data-testid={`${testid}-remove-${i}`}
              aria-label="Hapus foto"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label className={`flex h-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed text-[10px] font-semibold transition ${uploading ? "border-tosca-300 text-tosca-400" : "border-slate-200 text-slate-400 hover:border-tosca-500 hover:text-tosca-600"}`} data-testid={testid}>
          {uploading ? "Mengupload..." : <><Plus className="h-4 w-4" /> Tambah</>}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={add} disabled={uploading} />
        </label>
      </div>
    </div>
  );
}

function ItineraryEditor({ items = [], onChange, testid = "admin-itinerary" }) {
  const update = (i, k, v) => onChange(items.map((d, x) => (x === i ? { ...d, [k]: v } : d)));
  const renumber = (arr) => arr.map((d, x) => ({ ...d, day: x + 1 }));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(renumber(arr));
  };
  const remove = (i) => onChange(renumber(items.filter((_, x) => x !== i)));
  const add = () => onChange([...items, { day: items.length + 1, title: "", desc: "" }]);

  return (
    <div>
      <label className="label">Itinerary (Day by Day)</label>
      <div className="space-y-2">
        {items.map((d, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-cream p-3" data-testid={`${testid}-day-${i + 1}`}>
            <div className="flex items-center gap-2">
              <span className="chip flex-shrink-0 bg-tosca-600 text-white">Day {i + 1}</span>
              <input
                className="field !py-1.5 !text-xs"
                placeholder="Judul (cth. City Tour)"
                value={d.title || ""}
                onChange={(e) => update(i, "title", e.target.value)}
                data-testid={`${testid}-title-${i}`}
              />
              <button type="button" onClick={() => move(i, -1)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-tosca-600" aria-label="Pindah ke atas" data-testid={`${testid}-up-${i}`}>
                <ArrowUp className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => move(i, 1)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-tosca-600" aria-label="Pindah ke bawah" data-testid={`${testid}-down-${i}`}>
                <ArrowDown className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => remove(i)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-500" aria-label="Hapus hari" data-testid={`${testid}-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <textarea
              className="field mt-2 min-h-14 !text-xs"
              placeholder="Deskripsi aktivitas hari ini..."
              value={d.desc || ""}
              onChange={(e) => update(i, "desc", e.target.value)}
              data-testid={`${testid}-desc-${i}`}
            />
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="btn btn-outline mt-2 !px-4 !py-2 !text-xs" data-testid={`${testid}-add-day`}>
        <Plus className="h-3.5 w-3.5" /> Tambah Hari
      </button>
    </div>
  );
}

function TestimonialModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(
    item || { name: "", trip: "", rating: 5, review: "", photo: "", order: 99, is_active: true }
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.review.trim()) {
      toast.error("Nama dan review wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      if (item?.id) await api.put(`/admin/testimonials/${item.id}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success("Testimoni tersimpan.");
      onSaved();
    } catch {
      toast.error("Gagal menyimpan testimoni.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm" data-testid="admin-testimonial-modal">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-elevated sm:p-8">
        <h3 className="font-heading text-lg font-bold text-charcoal">{item?.id ? "Edit" : "Tambah"} Testimoni</h3>
        <div className="mt-5 space-y-4">
          <Field label="Nama Customer" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="cth. Budi Santoso" testid="admin-testi-name" />
          <Field label="Trip (cth. Trip China)" value={form.trip} onChange={(v) => setForm({ ...form, trip: v })} testid="admin-testi-trip" />
          <div>
            <label className="label">Rating</label>
            <div className="flex gap-1.5" data-testid="admin-testi-rating">
              {[1, 2, 3, 4, 5].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm({ ...form, rating: r })}
                  className="rounded-lg p-1.5 transition hover:bg-gold-light"
                  data-testid={`admin-testi-rating-${r}`}
                  aria-label={`Rating ${r}`}
                >
                  <Star className={`h-6 w-6 ${r <= form.rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Review</label>
            <textarea className="field min-h-24" value={form.review} onChange={(e) => setForm({ ...form, review: e.target.value })} placeholder="Ulasan pelanggan..." data-testid="admin-testi-review" />
          </div>
          <ImageUpload value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testid="admin-testi-photo" label="Foto Customer (opsional)" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Urutan" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: Number(v) })} testid="admin-testi-order" />
            <div>
              <label className="label">Status</label>
              <select className="field" value={form.is_active ? "true" : "false"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "true" })} data-testid="admin-testi-active">
                <option value="true">Tampil</option>
                <option value="false">Sembunyikan</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn btn-outline flex-1" data-testid="admin-testi-cancel">Batal</button>
            <button onClick={save} disabled={saving} className="btn btn-primary flex-1" data-testid="admin-testi-save">
              {saving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginErr, setLoginErr] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("inquiries");
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [files, setFiles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [editTesti, setEditTesti] = useState(null);
  const [settings, setSettings] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [editKind, setEditKind] = useState("destination");
  const [mediaUploading, setMediaUploading] = useState(false);
  const mediaInputRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const loadAll = async () => {
    try {
      const [s, i, d, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/inquiries"),
        api.get("/destinations"),
        api.get("/packages"),
      ]);
      setStats(s.data);
      setInquiries(i.data);
      setDestinations(d.data);
      setPackages(p.data);
    } catch {
      toast.error("Gagal memuat data admin.");
    }
  };

  const loadFiles = async () => {
    try {
      const { data } = await api.get("/admin/files");
      setFiles(data);
    } catch {
      toast.error("Gagal memuat media.");
    }
  };

  const loadTestimonials = async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setTestimonials(data);
    } catch {
      toast.error("Gagal memuat testimoni.");
    }
  };

  useEffect(() => {
    if (user) {
      loadAll();
      loadFiles();
      loadTestimonials();
      api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setLoginLoading(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      setUser(data);
      toast.success("Selamat datang, Admin Beyond Travel.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginErr(typeof d === "string" ? d : "Login gagal. Periksa email dan password.");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries(inquiries.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status inquiry diperbarui.");
    } catch {
      toast.error("Gagal memperbarui status.");
    }
  };

  const updateStatus = async (kind, id, status) => {
    const path = kind === "destination" ? "destinations" : "packages";
    try {
      await api.patch(`/admin/${path}/${id}/status`, { status });
      if (kind === "destination") setDestinations(destinations.map((d) => (d.id === id ? { ...d, status } : d)));
      else setPackages(packages.map((p) => (p.id === id ? { ...p, status } : p)));
      toast.success("Status diperbarui.");
    } catch {
      toast.error("Gagal memperbarui status.");
    }
  };

  const saveEdit = async () => {
    const path = editKind === "destination" ? "destinations" : "packages";
    const payload = { ...editItem };
    delete payload.id;
    payload.sample_itinerary = (Array.isArray(payload.sample_itinerary) ? payload.sample_itinerary : [])
      .map((d, i) => ({ ...d, day: i + 1 }));
    try {
      if (editItem.id) await api.put(`/admin/${path}/${editItem.id}`, payload);
      else await api.post(`/admin/${path}`, payload);
      toast.success("Tersimpan.");
      setEditItem(null);
      loadAll();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Gagal menyimpan.");
    }
  };

  const deleteTesti = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setTestimonials(testimonials.filter((t) => t.id !== id));
      toast.success("Testimoni dihapus.");
    } catch {
      toast.error("Gagal menghapus testimoni.");
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings tersimpan.");
    } catch {
      toast.error("Gagal menyimpan settings.");
    }
  };

  const uploadMedia = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMediaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/admin/upload", fd);
      toast.success("File berhasil diupload.");
      loadFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal. Coba lagi.");
    } finally {
      setMediaUploading(false);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(
        getFileUrl(url),
      );

      toast.success("URL disalin ke clipboard.");
    } catch {
      toast.error("Gagal menyalin URL.");
    }
  };

  const deleteMedia = async (id) => {
    try {
      await api.delete(`/admin/files/${id}`);
      setFiles(files.filter((f) => f.id !== id));
      toast.success("File dihapus.");
    } catch {
      toast.error("Gagal menghapus file.");
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="h-8 w-8 animate-spin text-tosca-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tosca-900 px-5">
        <div className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-elevated" data-testid="admin-login-panel">
          <img src="/logo.svg" alt="Beyond Travel" className="h-12 w-12 rounded-xl" />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-charcoal">Admin Beyond Travel</h1>
          <p className="mt-1 text-sm text-slate-500">Masuk untuk mengelola destinasi, paket, dan inquiry.</p>
          <form onSubmit={login} className="mt-6 space-y-4">
            <div>
              <label className="label">Email</label>
              <input type="email" className="field" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="admin@beyondtravel.co.id" data-testid="admin-email-input" />
            </div>
            <div>
              <label className="label">Password</label>
              <input type="password" className="field" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" data-testid="admin-password-input" />
            </div>
            {loginErr && <p className="rounded-xl bg-red-50 p-3 text-xs text-red-600" data-testid="admin-login-error">{loginErr}</p>}
            <button type="submit" disabled={loginLoading} className="btn btn-primary w-full" data-testid="admin-login-submit">
              {loginLoading ? "Memproses..." : "Login"}
            </button>
            <a href="/" className="block text-center text-xs text-slate-400 underline underline-offset-2 hover:text-tosca-600">Kembali ke website</a>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 glass border-b">
        <div className="container-x flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="" className="h-9 w-9 rounded-lg" />
            <p className="font-heading text-sm font-extrabold text-charcoal">
              BEYOND <span className="text-gold-dark">TRAVEL</span> <span className="ml-1 rounded-full bg-tosca-50 px-2 py-0.5 text-[10px] font-semibold text-tosca-700">ADMIN</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="hidden text-xs text-slate-500 hover:text-tosca-600 sm:block">Lihat Website</a>
            <button onClick={logout} className="btn btn-outline !px-4 !py-2 !text-xs" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
          {[
            { icon: Inbox, label: "Total Inquiries", value: stats?.total_inquiries ?? "-" },
            { icon: MessageSquareText, label: "Inquiry Baru", value: stats?.new_inquiries ?? "-" },
            { icon: Users2, label: "Destinasi", value: stats?.destinations ?? "-" },
            { icon: Send, label: "Paket LA", value: stats?.packages ?? "-" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-crystal" data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-tosca-600 text-gold">
                <s.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
                <p className="font-heading text-2xl font-extrabold text-charcoal">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`chip flex items-center gap-2 px-5 py-2.5 whitespace-nowrap transition ${
                tab === t.key ? "bg-tosca-600 text-white" : "bg-white text-slate-500 hover:text-tosca-700"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "inquiries" && (
          <div className="mt-6 overflow-x-auto rounded-3xl bg-white shadow-crystal" data-testid="admin-inquiries-table">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="p-4">Tanggal</th>
                  <th className="p-4">Tipe</th>
                  <th className="p-4">Nama / WA</th>
                  <th className="p-4">Destinasi</th>
                  <th className="p-4">Pax</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((q) => (
                  <tr key={q.id} className="border-b border-slate-50 hover:bg-tosca-50/40" data-testid={`admin-lead-row-${q.id}`}>
                    <td className="p-4 text-xs text-slate-500">{q.created_at ? new Date(q.created_at).toLocaleDateString("id-ID") : "-"}</td>
                    <td className="p-4"><span className="chip bg-tosca-50 text-tosca-700">{q.type}</span></td>
                    <td className="p-4">
                      <p className="font-semibold text-charcoal">{q.name || "-"}</p>
                      <p className="text-xs text-slate-400">{q.whatsapp || "-"}</p>
                    </td>
                    <td className="p-4 text-slate-600">{q.destination || "-"}</td>
                    <td className="p-4 text-slate-600">{q.pax || "-"}</td>
                    <td className="p-4">
                      <select
                        value={q.status}
                        onChange={(e) => updateInquiryStatus(q.id, e.target.value)}
                        className="field !py-1.5 !text-xs"
                        data-testid={`admin-lead-status-${q.id}`}
                      >
                        {INQUIRY_STATUSES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {inquiries.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-sm text-slate-400">Belum ada inquiry masuk.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {(tab === "destinations" || tab === "packages") && (
          <div className="mt-6" data-testid={`admin-${tab}-list`}>
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => {
                  setEditKind(tab === "destinations" ? "destination" : "package");
                  setEditItem(tab === "destinations"
                    ? { slug: "", name: "", region: "", short_desc: "", hero_image: "", gallery: [], status: "Available", featured: false, order: 99, sample_itinerary: [] }
                    : { slug: "", title: "", destination_slug: "", destination_name: "", duration: "", trip_type: "", hero_image: "", status: "Available", featured: false, order: 99, sample_itinerary: [] });
                }}
                className="btn btn-primary !py-2.5 !text-[13px]"
                data-testid={`admin-add-${tab === "destinations" ? "destination" : "package"}-button`}
              >
                + Tambah {tab === "destinations" ? "Destinasi" : "Paket"}
              </button>
            </div>
            <div className="grid gap-3">
              {(tab === "destinations" ? destinations : packages).map((item) => (
                <div key={item.id} className="flex flex-wrap items-center gap-4 rounded-2xl bg-white p-4 shadow-crystal" data-testid={`admin-item-${item.slug}`}>
                  <img src={item.hero_image} alt="" className="h-14 w-20 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-sm font-bold text-charcoal">{item.name || item.title}</p>
                    <p className="text-xs text-slate-400">/{item.slug}</p>
                  </div>
                  <span className={`chip ${item.featured ? "bg-gold-light text-gold-dark" : "bg-slate-100 text-slate-400"}`}>
                    {item.featured ? "Featured" : "Standard"}
                  </span>
                  <select
                    value={item.status}
                    onChange={(e) => updateStatus(tab === "destinations" ? "destination" : "package", item.id, e.target.value)}
                    className="field !w-auto !py-1.5 !text-xs"
                    data-testid={`admin-item-status-${item.slug}`}
                  >
                    {CONTENT_STATUSES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      setEditKind(tab === "destinations" ? "destination" : "package");
                      setEditItem({ ...item });
                    }}
                    className="btn btn-outline !px-4 !py-2 !text-xs"
                    data-testid={`admin-item-edit-${item.slug}`}
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "media" && (
          <div className="mt-6" data-testid="admin-media-library">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-slate-500">{files.length} file tersimpan. Klik "Salin URL" lalu paste di field foto destinasi/paket.</p>
              <label className="btn btn-primary !py-2.5 !text-[13px] cursor-pointer" data-testid="admin-media-upload-button">
                {mediaUploading ? "Mengupload..." : <><Upload className="h-4 w-4" /> Upload File</>}
                <input ref={mediaInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadMedia} disabled={mediaUploading} data-testid="admin-media-upload-input" />
              </label>
            </div>
            {files.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {files.map((f) => (
                  <div key={f.id} className="overflow-hidden rounded-2xl bg-white shadow-crystal" data-testid={`admin-media-item-${f.id.slice(0, 8)}`}>
                    <img src={f.url} alt={f.original_filename} className="h-36 w-full object-cover" loading="lazy" />
                    <div className="p-3">
                      <p className="truncate text-xs font-semibold text-charcoal">{f.original_filename || f.storage_path}</p>
                      <p className="text-[11px] text-slate-400">{(f.size / 1024).toFixed(0)} KB · {f.created_at ? new Date(f.created_at).toLocaleDateString("id-ID") : ""}</p>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => copyUrl(f.url)} className="btn btn-outline !flex-1 !px-3 !py-1.5 !text-[11px]" data-testid={`admin-media-copy-${f.id.slice(0, 8)}`}>
                          <Copy className="h-3 w-3" /> Salin URL
                        </button>
                        <button onClick={() => deleteMedia(f.id)} className="btn !bg-red-50 !px-3 !py-1.5 !text-[11px] !text-red-600 hover:!bg-red-100" data-testid={`admin-media-delete-${f.id.slice(0, 8)}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-3xl bg-white p-10 text-center shadow-crystal">
                <ImageIcon className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm text-slate-400">Belum ada file. Upload foto pertama Anda.</p>
              </div>
            )}
          </div>
        )}

        {tab === "testimonials" && (
          <div className="mt-6" data-testid="admin-testimonials-list">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-slate-500">Testimoni berstatus "Tampil" akan muncul di halaman Home menggantikan placeholder.</p>
              <button
                onClick={() => setEditTesti({ name: "", trip: "", rating: 5, review: "", photo: "", order: 99, is_active: true })}
                className="btn btn-primary !py-2.5 !text-[13px]"
                data-testid="admin-add-testimonial-button"
              >
                + Tambah Testimoni
              </button>
            </div>
            {testimonials.length > 0 ? (
              <div className="grid gap-3">
                {testimonials.map((t) => (
                  <div key={t.id} className="flex flex-wrap items-center gap-4 rounded-2xl bg-white p-4 shadow-crystal" data-testid={`admin-testi-row-${t.id.slice(0, 8)}`}>
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-tosca-600 font-heading text-xs font-bold text-white">
                        {(t.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-heading text-sm font-bold text-charcoal">
                        {t.name} <span className="text-xs font-normal text-slate-400">· {t.trip}</span>
                      </p>
                      <p className="truncate text-xs text-slate-500">{t.review}</p>
                    </div>
                    <span className="flex gap-0.5">
                      {[...Array(t.rating)].map((_, s) => (
                        <Star key={s} className="h-3.5 w-3.5 fill-gold text-gold" />
                      ))}
                    </span>
                    <span className={`chip ${t.is_active ? "bg-tosca-50 text-tosca-700" : "bg-slate-100 text-slate-400"}`}>
                      {t.is_active ? "Tampil" : "Tersembunyi"}
                    </span>
                    <button onClick={() => setEditTesti(t)} className="btn btn-outline !px-4 !py-2 !text-xs" data-testid={`admin-testi-edit-${t.id.slice(0, 8)}`}>
                      Edit
                    </button>
                    <button onClick={() => deleteTesti(t.id)} className="btn !bg-red-50 !px-3 !py-2 !text-xs !text-red-600 hover:!bg-red-100" data-testid={`admin-testi-delete-${t.id.slice(0, 8)}`} aria-label="Hapus">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-3xl bg-white p-10 text-center shadow-crystal">
                <MessageSquareQuote className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm text-slate-400">Belum ada testimoni. Home menampilkan placeholder sampai ada testimoni asli.</p>
              </div>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div className="mt-6 max-w-xl rounded-3xl bg-white p-6 shadow-crystal sm:p-8" data-testid="admin-settings-form">
            <h3 className="font-heading text-lg font-bold text-charcoal">Pengaturan CTA WhatsApp</h3>
            <p className="mt-1 text-sm text-slate-500">Nomor dan identitas kontak yang tampil di seluruh website.</p>
            <div className="mt-6 space-y-4">
              <Field label="Nomor WhatsApp (tanpa +, contoh 6288xxxx)" value={settings.whatsapp_number} onChange={(v) => setSettings({ ...settings, whatsapp_number: v })} testid="admin-settings-wa-number" />
              <Field label="Tampilan WhatsApp" value={settings.whatsapp_display} onChange={(v) => setSettings({ ...settings, whatsapp_display: v })} testid="admin-settings-wa-display" />
              <Field label="Email" value={settings.email} onChange={(v) => setSettings({ ...settings, email: v })} testid="admin-settings-email" />
              <Field label="Instagram" value={settings.instagram} onChange={(v) => setSettings({ ...settings, instagram: v })} testid="admin-settings-instagram" />
              <Field label="TikTok" value={settings.tiktok} onChange={(v) => setSettings({ ...settings, tiktok: v })} testid="admin-settings-tiktok" />
              <Field label="Alamat" value={settings.address} onChange={(v) => setSettings({ ...settings, address: v })} testid="admin-settings-address" />

              <div className="border-t border-slate-100 pt-5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Notifikasi WhatsApp Admin (Twilio)</p>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
                  Opsional — isi untuk menerima ping WhatsApp ke admin setiap ada inquiry masuk. Butuh akun Twilio (console.twilio.com) dengan WhatsApp sender aktif (sandbox/production). Kosongkan untuk menonaktifkan.
                </p>
                <div className="mt-3 space-y-3">
                  <Field label="Twilio Account SID" value={settings.twilio_account_sid} onChange={(v) => setSettings({ ...settings, twilio_account_sid: v })} placeholder="ACxxxxxxxxxxxxxxxx" testid="admin-settings-twilio-sid" />
                  <Field label="Twilio Auth Token" type="password" value={settings.twilio_auth_token} onChange={(v) => setSettings({ ...settings, twilio_auth_token: v })} placeholder="••••••••" testid="admin-settings-twilio-token" />
                  <Field label="Nomor WA Pengirim (Twilio)" value={settings.twilio_whatsapp_from} onChange={(v) => setSettings({ ...settings, twilio_whatsapp_from: v })} placeholder="whatsapp:+14155238886" testid="admin-settings-twilio-from" />
                  <Field label="Nomor WA Admin Penerima" value={settings.admin_whatsapp} onChange={(v) => setSettings({ ...settings, admin_whatsapp: v })} placeholder="62812xxxxxxxx" testid="admin-settings-admin-wa" />
                </div>
              </div>

              <button onClick={saveSettings} className="btn btn-primary w-full" data-testid="admin-settings-save-button">Simpan Settings</button>
            </div>
          </div>
        )}
      </div>

      {editItem && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm" data-testid="admin-edit-modal">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 shadow-elevated sm:p-8">
            <h3 className="font-heading text-lg font-bold text-charcoal">
              {editItem.id ? "Edit" : "Tambah"} {editKind === "destination" ? "Destinasi" : "Paket"}
            </h3>
            <div className="mt-5 space-y-4">
              <Field label="Slug (URL)" value={editItem.slug} onChange={(v) => setEditItem({ ...editItem, slug: v })} placeholder="cth. china-beijing-shanghai" testid="admin-edit-slug" />
              {editKind === "destination" ? (
                <>
                  <Field label="Nama Destinasi" value={editItem.name} onChange={(v) => setEditItem({ ...editItem, name: v })} testid="admin-edit-name" />
                  <Field label="Region" value={editItem.region} onChange={(v) => setEditItem({ ...editItem, region: v })} testid="admin-edit-region" />
                  <div>
                    <label className="label">Short Description</label>
                    <textarea className="field min-h-20" value={editItem.short_desc || ""} onChange={(e) => setEditItem({ ...editItem, short_desc: e.target.value })} data-testid="admin-edit-short-desc" />
                  </div>
                </>
              ) : (
                <>
                  <Field label="Judul Paket" value={editItem.title} onChange={(v) => setEditItem({ ...editItem, title: v })} testid="admin-edit-title" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Destination Slug" value={editItem.destination_slug} onChange={(v) => setEditItem({ ...editItem, destination_slug: v })} testid="admin-edit-dest-slug" />
                    <Field label="Destination Name" value={editItem.destination_name} onChange={(v) => setEditItem({ ...editItem, destination_name: v })} testid="admin-edit-dest-name" />
                    <Field label="Durasi (cth 5D4N)" value={editItem.duration} onChange={(v) => setEditItem({ ...editItem, duration: v })} testid="admin-edit-duration" />
                    <Field label="Trip Type" value={editItem.trip_type} onChange={(v) => setEditItem({ ...editItem, trip_type: v })} testid="admin-edit-trip-type" />
                  </div>
                </>
              )}
              {editKind === "destination" && (
                <GalleryEditor items={editItem.gallery || []} onChange={(g) => setEditItem({ ...editItem, gallery: g })} />
              )}
              <ImageUpload value={editItem.hero_image} onChange={(v) => setEditItem({ ...editItem, hero_image: v })} testid="admin-edit-hero-image" />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Urutan" type="number" value={editItem.order} onChange={(v) => setEditItem({ ...editItem, order: Number(v) })} testid="admin-edit-order" />
                <div>
                  <label className="label">Status</label>
                  <select className="field" value={editItem.status} onChange={(e) => setEditItem({ ...editItem, status: e.target.value })} data-testid="admin-edit-status">
                    {CONTENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Featured</label>
                  <select className="field" value={editItem.featured ? "true" : "false"} onChange={(e) => setEditItem({ ...editItem, featured: e.target.value === "true" })} data-testid="admin-edit-featured">
                    <option value="false">Tidak</option>
                    <option value="true">Ya</option>
                  </select>
                </div>
              </div>
              <ItineraryEditor items={editItem.sample_itinerary || []} onChange={(it) => setEditItem({ ...editItem, sample_itinerary: it })} />
              <div className="flex gap-3">
                <button onClick={() => setEditItem(null)} className="btn btn-outline flex-1" data-testid="admin-edit-cancel">Batal</button>
                <button onClick={saveEdit} className="btn btn-primary flex-1" data-testid="admin-edit-save">Simpan</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editTesti && (
        <TestimonialModal
          item={editTesti}
          onClose={() => setEditTesti(null)}
          onSaved={() => {
            setEditTesti(null);
            loadTestimonials();
          }}
        />
      )}
    </div>
  );
}
